import { useParams, Link } from 'react-router-dom';
import { videos, categories } from '@/mocks/videos';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const video = videos.find((v) => v.id === id);

  if (!video) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">视频未找到</h1>
          <p className="text-foreground-500 text-sm mb-6">该日韩视频内容不存在或已被移除</p>
          <Link
            to="/"
            className="inline-block px-6 py-2.5 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const relatedVideos = videos
    .filter((v) => v.id !== video.id && v.category === video.category)
    .slice(0, 4);

  const otherVideos = videos
    .filter((v) => v.id !== video.id && v.category !== video.category)
    .slice(0, 4);

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: video.categoryLabel, to: `/category/${video.category}` },
    { label: video.title },
  ];

  return (
    <main>
      <Breadcrumb items={breadcrumbItems} />

      {/* Article Content */}
      <article className="w-full px-4 md:px-6 py-8">
        <div className="max-w-[1000px] mx-auto">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Content */}
            <div className="flex-1 min-w-0">
              <div className="relative rounded-xl overflow-hidden mb-6" style={{ paddingBottom: '56.25%' }}>
                <img
                  src={video.thumbnail}
                  alt={`${video.title} - ${video.categoryLabel}日韩视频在线观看`}
                  title={`${video.title} - ${video.categoryLabel}高清日韩视频`}
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-white/90 flex items-center justify-center cursor-pointer hover:bg-white transition-all hover:scale-110 duration-300">
                  <i className="ri-play-fill text-primary-500 text-3xl ml-0.5"></i>
                </div>
                <div className="absolute bottom-4 left-4 right-4 flex items-center gap-3 flex-wrap">
                  <span className="px-3 py-1 rounded-full bg-primary-500 text-white text-xs font-medium">
                    {video.categoryLabel}
                  </span>
                  {video.isHot && (
                    <span className="px-3 py-1 rounded-full bg-accent-500 text-white text-xs font-medium">热门</span>
                  )}
                  {video.isNew && (
                    <span className="px-3 py-1 rounded-full bg-white/20 text-white text-xs font-medium backdrop-blur-sm">NEW</span>
                  )}
                </div>
              </div>

              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                {video.title}
              </h1>

              <div className="flex items-center gap-4 flex-wrap text-sm text-foreground-600 mb-6">
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500 w-4 h-4 flex items-center justify-center"></i>
                  <strong className="text-foreground-900">{video.rating}</strong> 分
                </span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>{video.year}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>{video.region}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>{video.duration}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
                  {video.views.toLocaleString()} 次观看
                </span>
              </div>

              <div className="flex items-center gap-2 flex-wrap mb-6">
                {video.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="prose prose-sm max-w-none mb-8">
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-3">日韩视频简介</h2>
                <p className="text-foreground-700 leading-relaxed text-sm">{video.description}</p>
              </div>

              <div className="flex items-center gap-3 pt-6 border-t border-background-200">
                <span className="text-sm text-foreground-500">分享日韩视频：</span>
                <button className="w-9 h-9 rounded-full bg-background-100 hover:bg-primary-100 flex items-center justify-center transition-colors cursor-pointer" aria-label="分享到微博">
                  <i className="ri-weibo-line text-foreground-600"></i>
                </button>
                <button className="w-9 h-9 rounded-full bg-background-100 hover:bg-primary-100 flex items-center justify-center transition-colors cursor-pointer" aria-label="分享到微信">
                  <i className="ri-wechat-line text-foreground-600"></i>
                </button>
                <button className="w-9 h-9 rounded-full bg-background-100 hover:bg-primary-100 flex items-center justify-center transition-colors cursor-pointer" aria-label="复制链接">
                  <i className="ri-link text-foreground-600"></i>
                </button>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="lg:w-[320px] flex-shrink-0">
              <div className="lg:sticky lg:top-24 space-y-8">
                {/* Video Info */}
                <div className="bg-background-100 rounded-xl p-5">
                  <h3 className="font-semibold text-foreground-950 mb-3 text-sm">视频信息</h3>
                  <dl className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-foreground-500">分类</dt>
                      <dd className="text-foreground-900 font-medium">{video.categoryLabel}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-foreground-500">年份</dt>
                      <dd className="text-foreground-900">{video.year}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-foreground-500">地区</dt>
                      <dd className="text-foreground-900">{video.region}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-foreground-500">时长</dt>
                      <dd className="text-foreground-900">{video.duration}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-foreground-500">评分</dt>
                      <dd className="text-foreground-900 font-medium text-accent-600">{video.rating}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-foreground-500">更新日期</dt>
                      <dd className="text-foreground-900">{video.updateDate}</dd>
                    </div>
                  </dl>
                </div>

                {/* Related Videos */}
                {relatedVideos.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-foreground-950 mb-4 text-sm">
                      同类日韩{video.categoryLabel}推荐
                    </h3>
                    <div className="space-y-3">
                      {relatedVideos.map((rv) => (
                        <Link
                          key={rv.id}
                          to={`/article/${rv.id}`}
                          className="flex gap-3 group cursor-pointer"
                        >
                          <div className="w-20 h-14 rounded-lg overflow-hidden flex-shrink-0">
                            <img
                              src={rv.thumbnail}
                              alt={rv.title}
                              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-xs font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors">{rv.title}</h4>
                            <div className="flex items-center gap-2 mt-1 text-xs text-foreground-500">
                              <span className="flex items-center gap-0.5">
                                <i className="ri-star-fill text-accent-500 w-3 h-3 flex items-center justify-center"></i>
                                {rv.rating}
                              </span>
                              <span>{rv.year}</span>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      </article>

      {/* More Recommendations */}
      <section className="w-full px-4 md:px-6 py-12 bg-background-100">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
            更多日韩视频推荐
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {otherVideos.map((ov) => (
              <Link
                key={ov.id}
                to={`/article/${ov.id}`}
                className="group cursor-pointer"
              >
                <div className="relative rounded-lg overflow-hidden mb-3" style={{ paddingBottom: '56.25%' }}>
                  <img
                    src={ov.thumbnail}
                    alt={ov.title}
                    className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <span className="absolute top-2 left-2 px-2 py-0.5 rounded text-xs bg-black/50 text-white backdrop-blur-sm">
                    {ov.categoryLabel}
                  </span>
                </div>
                <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors">{ov.title}</h3>
                <div className="flex items-center gap-2 mt-1 text-xs text-foreground-500">
                  <span className="flex items-center gap-0.5">
                    <i className="ri-star-fill text-accent-500 w-3 h-3 flex items-center justify-center"></i>
                    {ov.rating}
                  </span>
                  <span>{ov.region}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}